import { createSlice } from '@reduxjs/toolkit';
import { REDUX_ASYNC_STATUSES } from '../../consts/consts';

const { IDLE, LOADING, SUCCEEDED, FAILED } = REDUX_ASYNC_STATUSES;

const initialState = {
  currentUser: null,
  loginWithPasswordStatus: IDLE,
  loginWithMicrosoftStatus: IDLE,
  loginWithPasswordAndApproveTermsOfUseStatus: IDLE,
  loginWithMicrosoftAndApproveTermsOfUseStatus: IDLE,
  refreshMicrosoftSessionStatus: IDLE,
  validateUserStatus: IDLE,

  uiRights: {},
  getUiRightsStatus: IDLE,

  allowedSessionTypes: [],
  allowedSessionTypesStatus: IDLE,

  approveTermsOfUseStatus: IDLE
};

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    loginWithPassword: (state) => {
      state.loginWithPasswordStatus = LOADING;
    },
    loginWithPasswordSucceeded: (state, action) => {
      state.currentUser = action.payload;
      state.loginWithPasswordStatus = SUCCEEDED;
    },
    loginWithPasswordFailed: (state) => {
      state.loginWithPasswordStatus = FAILED;
    },

    loginWithPasswordAndApproveTermsOfUse: (state) => {
      state.loginWithPasswordAndApproveTermsOfUseStatus = LOADING;
    },
    loginWithPasswordAndApproveTermsOfUseSucceeded: (state, action) => {
      state.loginWithPasswordAndApproveTermsOfUseStatus = SUCCEEDED;
      state.currentUser = action.payload;
    },
    loginWithPasswordAndApproveTermsOfUseFailed: (state) => {
      state.loginWithPasswordAndApproveTermsOfUseStatus = FAILED;
    },

    loginWithMicrosoft: (state) => {
      state.loginWithMicrosoftStatus = LOADING;
    },
    loginWithMicrosoftSucceeded: (state, action) => {
      state.currentUser = action.payload;
      state.loginWithMicrosoftStatus = SUCCEEDED;
    },
    loginWithMicrosoftFailed: (state) => {
      state.loginWithMicrosoftStatus = FAILED;
    },

    // Action params: { accessToken, navigate }
    loginWithMicrosoftAndApproveTermsOfUse: (state) => {
      state.loginWithMicrosoftAndApproveTermsOfUseStatus = LOADING;
    },
    loginWithMicrosoftAndApproveTermsOfUseSucceeded: (state, action) => {
      state.loginWithMicrosoftAndApproveTermsOfUseStatus = SUCCEEDED;
      state.currentUser = action.payload;
    },
    loginWithMicrosoftAndApproveTermsOfUseFailed: (state) => {
      state.loginWithMicrosoftAndApproveTermsOfUseStatus = FAILED;
    },

    // Action params: { accessToken }
    refreshMicrosoftSession: (state) => {
      state.refreshMicrosoftSessionStatus = LOADING;
    },
    refreshMicrosoftSessionSucceeded: (state, action) => {
      state.currentUser = action.payload;
      state.refreshMicrosoftSessionStatus = SUCCEEDED;
    },
    refreshMicrosoftSessionFailed: (state) => {
      state.refreshMicrosoftSessionStatus = FAILED;
    },

    validateUser: (state) => {
      state.validateUserStatus = LOADING;
    },
    validateUserSucceeded: (state, action) => {
      state.currentUser = action.payload;
      state.validateUserStatus = SUCCEEDED;
    },
    validateUserFailed: (state) => {
      state.currentUser = null;
      state.validateUserStatus = FAILED;
    },

    // Patcht den in currentUser.meta_data gecachten Org-Namen lokal (z. B. nachdem der
    // User seine eigene Org umbenannt hat). Bewusst KEIN validateUser-Roundtrip: das würde
    // validateUserStatus auf LOADING setzen, wodurch router.js die ganze App kurz als leeres
    // Fragment rendert (Flackern). Hier wird nur der Anzeigename ohne Reload aktualisiert.
    setCurrentUserOrganizationName: (state, action) => {
      const organizationNameItem = state.currentUser?.meta_data?.find((item) => item.rel === 'organization_name');
      if (organizationNameItem) {
        organizationNameItem.description = action.payload;
      }
    },

    logout: () => {},
    logoutSucceeded: () => initialState,

    // Action params: { currentUserEmail }
    getAllowedSessionTypes: (state) => {
      state.allowedSessionTypesStatus = LOADING;
    },
    getAllowedSessionTypesSucceeded: (state, action) => {
      state.allowedSessionTypes = action.payload;
      state.allowedSessionTypesStatus = SUCCEEDED;
    },
    getAllowedSessionTypesFailed: (state) => {
      state.allowedSessionTypes = [];
      state.allowedSessionTypesStatus = FAILED;
    },

    getUiRights: (state) => {
      state.getUiRightsStatus = LOADING;
    },
    getUiRightsSucceeded: (state, action) => {
      state.uiRights = action.payload;
      state.getUiRightsStatus = SUCCEEDED;
    },
    getUiRightsFailed: (state) => {
      state.getUiRightsStatus = FAILED;
    },

    approveTermsOfUse: (state) => {
      state.approveTermsOfUseStatus = LOADING;
    },
    approveTermsOfUseSucceeded: (state) => {
      state.approveTermsOfUseStatus = SUCCEEDED;
    },
    approveTermsOfUseFailed: (state) => {
      state.approveTermsOfUseStatus = FAILED;
    }
  }
});

export const {
  loginWithPassword,
  loginWithPasswordSucceeded,
  loginWithPasswordFailed,
  loginWithPasswordAndApproveTermsOfUse,
  loginWithPasswordAndApproveTermsOfUseSucceeded,
  loginWithPasswordAndApproveTermsOfUseFailed,
  loginWithMicrosoft,
  loginWithMicrosoftSucceeded,
  loginWithMicrosoftFailed,
  loginWithMicrosoftAndApproveTermsOfUse,
  loginWithMicrosoftAndApproveTermsOfUseSucceeded,
  loginWithMicrosoftAndApproveTermsOfUseFailed,
  refreshMicrosoftSession,
  refreshMicrosoftSessionSucceeded,
  refreshMicrosoftSessionFailed,
  validateUser,
  validateUserSucceeded,
  validateUserFailed,
  setCurrentUserOrganizationName,
  logout,
  logoutSucceeded,
  getAllowedSessionTypes,
  getAllowedSessionTypesSucceeded,
  getAllowedSessionTypesFailed,
  getUiRights,
  getUiRightsSucceeded,
  getUiRightsFailed,
  approveTermsOfUse,
  approveTermsOfUseSucceeded,
  approveTermsOfUseFailed
} = authenticationSlice.actions;

export default authenticationSlice.reducer;
